import { useTranslation } from 'react-i18next';
import { AlertTriangle, RotateCw } from 'lucide-react';
import { cn } from 'lib/utils';
import { Button } from 'components/ui/button';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogTitle,
} from 'components/ui/alert-dialog';
import { TONE_CLASSES } from 'components/admin/audit/pillTone';
import type { DeskAnalysis, EcmUser, Transition } from 'api/sectorAlignmentApi';

interface RealignDialogProps {
  desk: DeskAnalysis | null;
  deskName: string;
  pending: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: () => void;
}

/** « Camille Roux (SP1), Lucas Petit (SP2). » — one entry per ECM and sector, over every transition of the desk. */
const impactLine = (impact: Transition[], side: 'losing' | 'gaining'): string => {
  const names = impact.flatMap((transition) =>
    (transition[side] ?? []).map((user: EcmUser) => `${user.name} (${side === 'losing' ? transition.from ?? '—' : transition.to})`)
  );
  const distinct = Array.from(new Set(names));
  return distinct.length ? `${distinct.join(', ')}.` : '';
};

/** 7a: confirmation, focus on « Annuler »; confirm stays disabled until the API answers (AC13). */
const RealignDialog = ({ desk, deskName, pending, error, onCancel, onConfirm }: RealignDialogProps) => {
  const { t } = useTranslation();
  const count = desk?.misaligned ?? 0;
  const losing = impactLine(desk?.impact ?? [], 'losing');
  const gaining = impactLine(desk?.impact ?? [], 'gaining');
  const orphans = (desk?.ecm ?? []).flatMap((issue) => (issue.kind === 'sector-without-ecm' ? [issue] : []));

  return (
    <AlertDialog open={desk !== null} onOpenChange={(open) => !open && !pending && onCancel()}>
      <AlertDialogContent className="flex max-h-[calc(100dvh-2rem)] max-w-[480px] flex-col gap-2.5 rounded-[15px] bg-card">
        <AlertDialogTitle className="text-[17px]">
          {t('Realign {{count}} {{desk}} tickets?', { count, desk: deskName })}
        </AlertDialogTitle>
        <div
          data-testid="realign-scroll"
          tabIndex={0}
          className="flex min-h-0 flex-1 flex-col gap-2.5 overflow-y-auto rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring"
        >
        <AlertDialogDescription className="text-[13.5px] leading-relaxed">
          {t("The sector of {{count}} tickets (including {{closed}} closed) will be replaced by their store's current sector.", {
            count,
            closed: desk?.closed ?? 0,
          })}
        </AlertDialogDescription>
        <div className="flex flex-col gap-1 text-[13.5px]">
          {losing && (
            <p>
              <span className="text-muted-foreground">{t('Lose access:')}</span> {losing}
            </p>
          )}
          {gaining && (
            <p>
              <span className="text-muted-foreground">{t('Gain access:')}</span> {gaining}
            </p>
          )}
        </div>
        <p className="text-[13.5px] leading-relaxed text-muted-foreground">
          {t('The exact number is recomputed at launch: only tickets still misaligned will be changed.')}
          <br />
          {t('The action is recorded in the journal.')}
        </p>
        {orphans.map((orphan) => (
          <p
            key={orphan.sector}
            className={cn('flex items-start gap-2 rounded-[10px] px-3.5 py-3 text-[13px] font-medium', TONE_CLASSES.amber)}
          >
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            {t('No ECM is attached to {{sector}}: after realignment, {{count}} tickets will be visible to no ECM.', {
              sector: orphan.sector,
              count: orphan.tickets,
            })}
          </p>
        ))}
        {error && (
          <p role="alert" className="text-[13px] font-medium text-rose-700 dark:text-rose-400">
            {error}
          </p>
        )}
        </div>
        <AlertDialogFooter className="mt-2.5 gap-2.5">
          <AlertDialogCancel disabled={pending}>{t('Cancel')}</AlertDialogCancel>
          <Button onClick={onConfirm} disabled={pending}>
            {pending && <RotateCw className="animate-spin" aria-hidden="true" />}
            {t('Realign {{count}} tickets', { count })}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default RealignDialog;
